import { Component, OnInit } from '@angular/core';
import { CpMarketplaceApiService } from '../../services/cp-marketplace-api.service';
import { SharedDataService } from '../../services/shared-data.service';
import {
  investor_state_map,
  investor_action_awaited
} from '../../issuer/documentation/documentation-actions/documentation-maps';
import { Router, ActivatedRoute } from '@angular/router';
import {
  DOCUMENT_NAME_MAP, DEAL_CONFIRMATION_NOTE,
  IPA_CERTIFICATE, CORPORATE_ACTION_FORM,
  CAF_ISSUANCE_LETTER,
  MASTER_CREATION_FORM,
  LETTER_OF_OFFER,
  STATE_WAIT_DCN
} from '../global-meta-data';
import { forkJoin, of } from 'rxjs';
import { CpAuctionApiService } from 'appRootPath/services/cp-auction-api.service';
import { environment } from 'src/environments/environment';

const isAuctionEnabledForEnv = environment.isAuctionEnabled;

@Component({
  selector: 'app-actionables',
  templateUrl: './actionables.component.html',
  styleUrls: ['./actionables.component.scss']
})

export class ActionablesComponent implements OnInit {
  actionables = [];
  redirectType = 'nrd';
  currentDate;
  roleType;
  isBiddingEnabled;
  isLoading = false;
  dealsToAccept = [];
  investorStateMap = investor_state_map;
  investorActionAwaited = investor_action_awaited;
  issueDetails = null;
  openAuctionsCount = 0;
  openRfqLink = '/investor/price-discovery/';
  isAuctionEnabled = false;
  constructor(
    private apiService: CpMarketplaceApiService,
    private currentRoute: ActivatedRoute,
    private sharedData: SharedDataService,
    private auctionApi: CpAuctionApiService,
    private router: Router
  ) { }

  /**
   * Angular oninit lifecycle hook
   * Initializes roleType, isBiddingEnabled, currentDate
   * Gets Actionables based on roleType
   */
  ngOnInit() {
    this.currentRoute.params.subscribe(params => {
      this.redirectType = params.redirectType;
    });

    this.roleType = this.sharedData.getUserDataFromLS().roleType;
    this.isBiddingEnabled = this.sharedData.getUserDataFromLS().is_bidding_enabled;
    this.isLoading = true;
    this.getActionables();
    this.currentDate = new Date().toISOString().substring(0, 10);
    this.isAuctionEnabled =
      isAuctionEnabledForEnv && this.sharedData.getUserSelectionFromLS().is_auction_user;

  }

  /**
   * Navigate to documentation after delay
   */
  bidEventHandler(bidData) {
    if (bidData) {
      // this.toastr.success(ISSUER_MESSAGES.ACCEPTS_BID);
      setTimeout(() => {
        this.router.navigate(['investor/documentation/']);
      }, 700);

    }
  }

  /**
   * Gets actionables based on roleType.
   */
  getActionables() {
    if (this.roleType === 1) {
      this.getIssuerActionables();
    } else if (this.roleType === 3) {
      this.getInvestorActionables();
    }
  }

  /**
   * Gets actionables for issuer
   */
  getIssuerActionables() {
    this.apiService.getCPDocInfo().subscribe(cpDocsResp => {
      this.actionables = this.parseIssuerActionables(cpDocsResp);
      this.isLoading = false;

      if (this.redirectType === 'rd' && this.actionables.length === 1) {
        this.router.navigate([this.actionables[0].link]);
      } else if (this.redirectType === 'rd' && this.actionables.length === 0) {
        this.router.navigate(['issuer', 'new-issue']);
      }
    });
  }

  /**
   * Parses CP documents and gets actionable docs.
   */
  parseIssuerActionables(cpDocs) {
    const actionables = [];

    if (!cpDocs || cpDocs.error || cpDocs.length === 0) {
      return [];
    }

    cpDocs.forEach(eachCp => {
      eachCp.cp.forEach(eachIssue => {
        if (!eachIssue.state_info && eachIssue.system_generated_docs
          && eachIssue.system_generated_docs.some(doc => doc.document_name === MASTER_CREATION_FORM)) {
          const mcfDoc = eachIssue.system_generated_docs.filter(
            doc => doc.document_name === MASTER_CREATION_FORM);
          if (this.isBiddingEnabled) {
            if (mcfDoc.is_signed) {
              actionables.push({
                message: `Send the signed ${DOCUMENT_NAME_MAP[MASTER_CREATION_FORM]} of
                 ${eachCp.name} to NSDL`,
                link: this.getLink(eachCp.id)
              });
            } else {
              actionables.push({
                message: `Review the ${DOCUMENT_NAME_MAP[MASTER_CREATION_FORM]} for CP:
                 ${eachCp.name} and send to signatories for signing.`,
                link: this.getLink(eachCp.id)
              });
            }
          }

        } else if (!eachIssue.state_info && !eachIssue.system_generated_docs) {
          // Do nothing
        } else {
          const issuerStateMap = this.getIssuerStateMap(eachCp.name, eachCp.ISIN_no);
          if (issuerStateMap.hasOwnProperty(eachIssue.state_info[0].state)) {
            actionables.push({
              message: issuerStateMap[eachIssue.state_info[0].state],
              link: this.getLink(eachCp.id),
              time: eachIssue.state_info[0].modified_at
            });
          }
        }
      });
    });

    return actionables;
  }

  /**
   * gets state map for actionables issuer role.
   */
  getIssuerStateMap(cpName, isin) {
    const issuer_state_map = {
      generate_and_sign_post_auction_docs: `Review ${DOCUMENT_NAME_MAP[DEAL_CONFIRMATION_NOTE]},${DOCUMENT_NAME_MAP[LETTER_OF_OFFER]}
       for CP: ${cpName} [ISIN: ${isin}] and send to signatories for signing.`,
      generate_and_sign_IPA_certificate_creation_docs: `Review documents to be sent to IPA for CP:
       ${cpName} [${isin}] and send to signatories for signing.`,
      send_IPA_certificate_to_investor: `Review ${DOCUMENT_NAME_MAP[IPA_CERTIFICATE]} for CP:
       ${cpName} [ISIN: ${isin}] and send to investor for approval.`,
      generate_and_sign_CAF: `Review ${DOCUMENT_NAME_MAP[CORPORATE_ACTION_FORM]} and
       ${DOCUMENT_NAME_MAP[CAF_ISSUANCE_LETTER]}
       for CP: ${cpName} [ISIN: ${isin}] and send to signatories for signing.`,
      send_CAF_to_RTA_and_NSDL: `Send ${DOCUMENT_NAME_MAP[CORPORATE_ACTION_FORM]} and
       ${DOCUMENT_NAME_MAP[CAF_ISSUANCE_LETTER]}
       for CP: ${cpName} [ISIN: ${isin}] and send to NSDL.`,
    };

    return issuer_state_map;
  }

  /**
   * Make API call to get investor actionables.
   */
  getInvestorActionables() {
    const apiCalls: any = [this.isAuctionEnabled ? this.auctionApi.getAuctionsWithBidsFilter(false) : of([])];
    apiCalls.push(this.apiService.getIssueDocInfo());

    forkJoin(apiCalls).subscribe(([openAuctions, issueDocsResp]) => {
      const openAuctionList: any = openAuctions;
      this.actionables = this.parseInvestorActionables(issueDocsResp);
      this.dealsToAccept = this.parseInvestorDeals(issueDocsResp);
      this.isLoading = false;
      this.openAuctionsCount = (openAuctionList && openAuctionList.length) || 0;
      if (this.redirectType === 'rd' && !this.dealsToAccept.length && this.actionables.length === 1) {
        this.router.navigate([this.actionables[0].link]);
      }
    });
  }

  /**
   * Parses investor deals.
   */
  parseInvestorDeals(issueDocs) {
    const arrDealsToAccept = [];
    issueDocs.forEach(issue => {
      if (issue.state_info && issue.state_info.length) {
        if (issue.state_info[0] && issue.state_info[0].state === STATE_WAIT_DCN) {
          arrDealsToAccept.push(issue);
        }
      }
    });
    return arrDealsToAccept;
  }

  /**
   * Parses issue docs and gets actionables for investor users.
   */
  parseInvestorActionables(issueDocs) {
    const actionables = [];
    issueDocs.forEach(issue => {
      const investorStateMessageMap =
        this.getInvestorStateMap(issue.issue_company[0].company_name, issue.cp.ISIN_no);

      if (issue.state_info && issue.system_generated_docs &&
        investorStateMessageMap.hasOwnProperty(issue.state_info[0].state)) {
        actionables.push({
          message: investorStateMessageMap[issue.state_info[0].state],
          link: this.getLink(),
          time: issue.state_info[0].modified_at
        });
      }
    });
    return actionables;
  }

  /**
   * state map for actionables.
   */
  getInvestorStateMap(companyName, isin) {
    const investor_state_actionables_map = {
      // eslint-disable-next-line max-len
      verify_post_auction_docs: `Review ${DOCUMENT_NAME_MAP[DEAL_CONFIRMATION_NOTE]} along with other documents for CP [ISIN: ${isin}] of ${companyName} and send to signatories for signing.`
    };

    return investor_state_actionables_map;
  }

  /**
   * Get page links to navigate to
   */
  getLink(cpId = null) {
    if (this.roleType === 1) {
      return `/issuer/documentation/${cpId}/pending-on-you`;
    } else if (this.roleType === 3) {
      return '/investor/documentation';
    }
  }
}
